import React, { useRef, useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import PhoneInput from 'react-phone-number-input';
import PhoneCountrySelect from 'components/PhoneCountrySelect';
import { useKeepPhoneDigitsOnCountryChange, keepCallingCodeOnKeyDown } from 'components/phoneCountryUtils';
import 'react-phone-number-input/style.css';
import '../styles.css';
import CustomDatePicker from '../local_components/CustomDatePicker';
import { ConfigProvider, Select, Input, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { COLORS } from 'components/UserDashboardV2/utils/constants';
import { fluid } from 'components/UserDashboardV2/utils/responsive';
import { formatPhoneNumber } from './utils';


import VideoCourseIconSvg from '../dashboardV3/video-course-icon.svg';
import StudyMaterialsIconSvg from '../dashboardV3/study-materials-icon.svg';
import TestSeriesIconSvg from '../dashboardV3/test-series-icon.svg';
import LiveCoursesIconSvg from '../dashboardV3/live-courses-icon.svg';

export const PhoneIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.10305 3.95004C7.83633 2.68332 5.78256 2.68332 4.51584 3.95004L4.03689 4.42899C3.39533 5.07055 2.97047 5.91596 3.0016 6.82273C3.11338 10.0781 4.66127 13.6302 7.51554 16.4845C10.3698 19.3387 13.9219 20.8866 17.1773 20.9984C18.084 21.0295 18.9294 20.6047 19.571 19.9631L20.05 19.4842C21.3167 18.2174 21.3167 16.1637 20.05 14.897L19.8609 14.7079C18.8488 13.6958 17.2533 13.5637 16.0886 14.3957C15.4434 14.8565 14.6284 15.0628 13.8846 14.7881C12.8685 14.4129 11.8911 13.7991 11.046 12.954C10.2009 12.1089 9.58715 11.1315 9.2119 10.1154C8.93725 9.37158 9.14348 8.55657 9.60432 7.91139C10.4363 6.74668 10.3042 5.15121 9.29212 4.13911L9.10305 3.95004Z" stroke="#636363" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const MailIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 8L9.39488 10.2384C10.3605 10.875 10.8433 11.1934 11.364 11.3153C11.8241 11.4231 12.3033 11.4207 12.7623 11.3082C13.2817 11.1809 13.7612 10.8576 14.7202 10.2111L18 8M14 20H10C7.19974 20 5.79961 20 4.73005 19.455C3.78924 18.9757 3.02433 18.2108 2.54497 17.27C2 16.2004 2 14.8003 2 12C2 9.19974 2 7.79961 2.54497 6.73005C3.02433 5.78924 3.78924 5.02433 4.73005 4.54497C5.79961 4 7.19974 4 10 4L14 4C16.8003 4 18.2004 4 19.27 4.54497C20.2108 5.02433 20.9757 5.78924 21.455 6.73005C22 7.79961 22 9.19974 22 12C22 14.8003 22 16.2004 21.455 17.27C20.9757 18.2108 20.2108 18.9757 19.27 19.455C18.2004 20 16.8003 20 14 20Z" stroke="#636363" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const LiveCourseIcon = ({ size = 32 }) => (
    <img src={LiveCoursesIconSvg} alt="" style={{ width: size, height: size, display: "block" }} />
);

export const TestSeriesIcon = ({ size = 32 }) => (
    <img src={TestSeriesIconSvg} alt="" style={{ width: size, height: size, display: "block" }} />
);

export const VideoCourseIcon = ({ size = 32 }) => (
    <img src={VideoCourseIconSvg} alt="" style={{ width: size, height: size, display: "block" }} />
);

export const StudyMaterialsIcon = ({ size = 32 }) => (
    <img src={StudyMaterialsIconSvg} alt="" style={{ width: size, height: size, display: "block" }} />
);

export const MoreVertIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#AEAEAE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <circle cx="12" cy="12" r="1" fill="#AEAEAE" />
        <circle cx="12" cy="5" r="1" fill="#AEAEAE" />
        <circle cx="12" cy="19" r="1" fill="#AEAEAE" />
    </svg>
);

export const ChevronDownIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
        <path d="M6 9L12 15L18 9" stroke="#666666" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const antIcon = <LoadingOutlined style={{ fontSize: 14 }} spin />;

export const PrimaryBadge = () => (
    <span style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: `${fluid(6)} ${fluid(10)}`,
        borderRadius: fluid(4),
        backgroundColor: COLORS.statusActiveBg,
        border: "1px solid #34D399",
        color: COLORS.statusActiveText,
        fontFamily: "Roboto, sans-serif",
        fontSize: fluid(12),
        fontWeight: 600,
        lineHeight: fluid(10),
        boxSizing: "border-box",
        whiteSpace: "nowrap",
    }}>
        Primary
    </span>
);

export const InfoField = ({ label, value, flex = 1, badge }) => (
    <div style={{
        flex,
        minWidth: 0,
        backgroundColor: "#F3F3F3",
        borderRadius: fluid(16),
        padding: fluid(16),
        display: "flex",
        flexDirection: "column",
        gap: fluid(9),
        boxSizing: "border-box",
    }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: fluid(6) }}>
            <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), fontWeight: 400, letterSpacing: 0, color: "#666666" }}>{label}</span>
            {badge && <PrimaryBadge />}
        </div>
        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 400, letterSpacing: 0, color: "#1A1A1A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {value || "NA"}
        </span>
    </div>
);

export const EditableInfoField = ({ label, value, type = "text", options = [], flex = 1, badge, isEditing, onChange, error, errorMessage, disabled = false, placeholder, showSearch = false, required = false }) => {
    const isPincode = String(label || "").toLowerCase().includes("pincode") || type === "pincode";
    const selectRef = useRef(null);
    // Phone fields: keep the typed number when the country is changed.
    const phoneCountryHandlers = useKeepPhoneDigitsOnCountryChange(value, (val) => onChange && onChange(val || "+91"));
    const [searchText, setSearchText] = useState("");
    const isSearching = Boolean(searchText && searchText.trim().length > 0);

    const normalizedOptions = useMemo(() => {
        if (!Array.isArray(options)) return [];
        return options.map(opt => (
            typeof opt === 'object' && opt !== null
                ? { label: opt.label ?? opt.value, value: opt.value }
                : { label: String(opt), value: opt }
        ));
    }, [options]);

    const scrollToSelected = useCallback(() => {
        const doScroll = () => {
            const dropdowns = document.querySelectorAll('.ant-select-dropdown:not(.ant-select-dropdown-hidden)');
            if (!dropdowns || dropdowns.length === 0) return false;
            const dropdown = dropdowns[dropdowns.length - 1];
            const selectedItem = dropdown.querySelector('.ant-select-item-option-selected');
            if (!selectedItem) return false;

            // Find scrollable container inside dropdown
            let container = selectedItem.closest('.rc-virtual-list-holder') || selectedItem.parentElement;
            while (container && container !== dropdown) {
                const overflowY = window.getComputedStyle(container).overflowY;
                if ((overflowY === 'auto' || overflowY === 'scroll') && container.scrollHeight > container.clientHeight) {
                    break;
                }
                container = container.parentElement;
            }
            if (!container || container === dropdown) {
                container = dropdown.querySelector('.rc-virtual-list-holder') || dropdown.querySelector('.ant-select-dropdown > div') || dropdown;
            }

            if (container && typeof container.scrollTop === 'number') {
                const itemRect = selectedItem.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();
                const relativeTop = (itemRect.top - containerRect.top) + container.scrollTop;
                const targetScroll = relativeTop - Math.floor(container.clientHeight / 2) + Math.floor(itemRect.height / 2);
                container.scrollTop = Math.max(0, targetScroll);
                return true;
            }
            return false;
        };

        // Scroll immediately and across multiple animation frames so the very first open is directly centered
        requestAnimationFrame(doScroll);
        [20, 50, 100, 160, 240, 350].forEach((delay) => {
            setTimeout(doScroll, delay);
        });
    }, []);

    const handleSelect = useCallback(() => {
        setSearchText("");
        const doBlur = () => {
            if (selectRef.current) {
                selectRef.current.blur?.();
            }
            if (document.activeElement && typeof document.activeElement.blur === 'function') {
                document.activeElement.blur();
            }
        };
        doBlur();
        setTimeout(doBlur, 20);
        setTimeout(doBlur, 60);
        setTimeout(doBlur, 150);
    }, []);

    const handleDropdownVisibleChange = useCallback((open) => {
        if (open) {
            setSearchText("");
            scrollToSelected();
        } else {
            setSearchText("");
            const doBlur = () => {
                if (selectRef.current) {
                    selectRef.current.blur?.();
                }
                if (document.activeElement && document.activeElement.classList?.contains('ant-select-selection-search-input')) {
                    document.activeElement.blur();
                }
            };
            doBlur();
            setTimeout(doBlur, 20);
            setTimeout(doBlur, 60);
            setTimeout(doBlur, 150);
        }
    }, [scrollToSelected]);

    return (
        <div
            style={{
                flex,
                minWidth: 0,
                backgroundColor: isEditing ? "#FFFFFF" : "#F3F3F3",
                borderRadius: fluid(16),
                padding: isEditing ? `${fluid(16)} ${fluid(16)} ${fluid(4)} ${fluid(16)}` : fluid(16),
                display: "flex",
                flexDirection: "column",
                gap: fluid(9),
                boxSizing: "border-box",
                position: "relative",
                justifyContent: "flex-start",
                opacity: disabled ? 0.6 : 1,
                cursor: disabled ? "not-allowed" : "default",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: fluid(6) }}>
                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), fontWeight: 400, letterSpacing: 0, color: "#666666" }}>
                    {label}
                    {required && <span style={{ color: "#FF414D", marginLeft: fluid(4) }}>*</span>}
                </span>
                {badge && <PrimaryBadge />}
            </div>
            {!isEditing ? (
                <div style={{ width: "100%", height: fluid(28), display: "flex", alignItems: "center" }}>
                    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 400, letterSpacing: 0, color: "#1A1A1A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {type === "date" && value
                            ? moment(value).format("DD MMM YYYY")
                            : type === "phone"
                                ? formatPhoneNumber(value)
                                : (value || "NA")}
                    </span>
                </div>
            ) : (
                <div style={{
                    width: "100%",
                    height: fluid(32),
                    display: "flex",
                    alignItems: "center",
                    boxSizing: "border-box",
                    borderBottom: type === "dropdown" || type === "date" ? "none" : "1px solid #E6E6E6",
                }}>
                    {type === "dropdown" ? (
                        <ConfigProvider virtual={false}>
                            <Select
                                ref={selectRef}
                                disabled={disabled}
                                showSearch={showSearch}
                                defaultActiveFirstOption={false}
                                listItemHeight={36}
                                listHeight={256}
                                options={normalizedOptions}
                                filterOption={showSearch ? (input, option) =>
                                    String(option?.label || option?.children || option?.value || '')
                                        .toLowerCase()
                                        .includes(String(input || '').toLowerCase())
                                    : undefined}
                                className={`no-border-select-v3 profile-field-select ${isSearching ? 'is-searching' : 'not-searching'}`}
                                style={{
                                    width: '100%',
                                    border: 'none',
                                    backgroundColor: 'transparent',
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                }}
                                dropdownStyle={{ borderRadius: fluid(12), overflow: 'hidden' }}
                                value={value || undefined}
                                placeholder={placeholder || "Select"}
                                onChange={onChange}
                                onSearch={(txt) => setSearchText(txt || "")}
                                onSelect={handleSelect}
                                onDropdownVisibleChange={handleDropdownVisibleChange}
                                suffixIcon={<ChevronDownIcon />}
                            >
                                {normalizedOptions.map(opt => (
                                    <Select.Option key={opt.value} value={opt.value} label={opt.label}>{opt.label}</Select.Option>
                                ))}
                            </Select>
                        </ConfigProvider>
                    ) : type === "date" ? (
                        <CustomDatePicker
                            value={value}
                            onChange={onChange}
                            disableFuture={label === "Date of birth"}
                            // Selected date in medium weight (slightly bold).
                            inputStyle={{ fontWeight: 600 }}
                        />
                    ) : type === "phone" ? (
                        <PhoneInput
                            className="PhoneInputV3"
                            defaultCountry="IN"
                            international
                            countryCallingCodeEditable={false}
                            placeholder="Enter mobile number"
                            value={value || "+91"}
                            onChange={phoneCountryHandlers.handleChange}
                            onCountryChange={phoneCountryHandlers.handleCountryChange}
                            addInternationalOption={true}
                            countrySelectComponent={PhoneCountrySelect}
                            // Backspace/Delete next to the locked "+91" must not wipe the number.
                            numberInputProps={{ onKeyDown: keepCallingCodeOnKeyDown }}
                            style={{ border: "none", width: "100%", height: "100%", display: "flex", alignItems: "center" }}
                            maxLength={(value?.startsWith('+91') || value?.startsWith('91')) && value?.length === 13 ? 11 : ""}
                        />
                    ) : (
                        <Input
                            bordered={false}
                            type="text"
                            className="profile-field-input"
                            // "Enter email", "Enter pincode", "Enter address", "Enter father's name"…
                            placeholder={placeholder || `Enter ${String(label || '').charAt(0).toLowerCase()}${String(label || '').slice(1)}`}
                            inputMode={isPincode ? "numeric" : undefined}
                            maxLength={isPincode ? 6 : undefined}
                            value={value || ""}
                            onChange={(e) => {
                                if (isPincode) {
                                    const numericOnly = e.target.value.replace(/\D/g, "").slice(0, 6);
                                    onChange(numericOnly);
                                } else {
                                    onChange(e.target.value);
                                }
                            }}
                            onKeyPress={isPincode ? (e) => {
                                if (!/[0-9]/.test(e.key)) {
                                    e.preventDefault();
                                }
                            } : undefined}
                            style={{
                                width: "100%",
                                height: "100%",
                                padding: 0,
                                margin: 0,
                                fontFamily: "Roboto, sans-serif",
                                fontSize: fluid(19),
                                color: "#1A1A1A",
                                outline: "none",
                                boxShadow: "none",
                                backgroundColor: "transparent",
                                display: "flex",
                                alignItems: "center",
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export const SectionCard = ({ title, isEditing, onEdit, onCancel, onSave, saving, saveDisabled, children, style = {} }) => (
    <div style={{
        backgroundColor: "#FFFFFF",
        borderRadius: fluid(24),
        padding: fluid(20),
        display: "flex",
        flexDirection: "column",
        gap: fluid(16),
        boxSizing: "border-box",
        ...style,
    }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(20), fontWeight: 500, letterSpacing: 0, lineHeight: fluid(20), color: "#191919" }}>{title}</span>
            {isEditing ? (
                <div style={{ display: "flex", gap: fluid(16), alignItems: "center" }}>
                    <button
                        type="button"
                        onClick={onCancel}
                        style={{
                            border: "none",
                            background: "transparent",
                            color: "#999999",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: fluid(16),
                            fontWeight: 700,
                            letterSpacing: 0,
                            lineHeight: "normal",
                            textTransform: "uppercase",
                            cursor: "pointer",
                            padding: 0,
                            display: "inline-flex",
                            alignItems: "center",
                        }}
                    >
                        CANCEL
                    </button>
                    <button
                        type="button"
                        onClick={(saving || saveDisabled) ? null : onSave}
                        disabled={saving || saveDisabled}
                        style={{
                            border: "none",
                            background: "transparent",
                            color: COLORS.primary,
                            opacity: (saving || saveDisabled) ? 0.5 : 1,
                            fontFamily: "Roboto, sans-serif",
                            fontSize: fluid(16),
                            fontWeight: 700,
                            letterSpacing: 0,
                            lineHeight: "normal",
                            textTransform: "uppercase",
                            cursor: (saving || saveDisabled) ? "not-allowed" : "pointer",
                            padding: 0,
                            display: "inline-flex",
                            alignItems: "center",
                        }}
                    >
                        {saving ? <Spin indicator={antIcon} /> : "SAVE"}
                    </button>
                </div>
            ) : onEdit && (
                <button
                    type="button"
                    onClick={onEdit}
                    style={{
                        border: "none",
                        background: "transparent",
                        color: COLORS.primary,
                        fontFamily: "Roboto, sans-serif",
                        fontSize: fluid(16),
                        fontWeight: 700,
                        letterSpacing: 0,
                        lineHeight: "normal",
                        textTransform: "uppercase",
                        cursor: "pointer",
                        padding: 0,
                        display: "inline-flex",
                        alignItems: "center",
                    }}
                >
                    EDIT
                </button>
            )}
        </div>
        {children}
    </div>
);

export const CourseBadge = ({ label, bg, border, color, fontFamily, fontWeight }) => (
    <span style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "flex-start",
        height: fluid(22),
        padding: `0 ${fluid(10)}`,
        borderRadius: fluid(4),
        backgroundColor: bg,
        border: `1px solid ${border}`,
        color,
        fontFamily,
        fontSize: fluid(12),
        fontWeight,
        lineHeight: fluid(18),
        letterSpacing: "0%",
        whiteSpace: "nowrap",
        boxSizing: "border-box",
    }}>
        {label}
    </span>
);

export const ProgressBar = ({ percent }) => (
    <div style={{ display: "flex", alignItems: "center", gap: fluid(16) }}>
        <div style={{ flex: 1, minWidth: 0, height: fluid(8), borderRadius: fluid(100), backgroundColor: "#E6E6E6", overflow: "hidden" }}>
            <div style={{ width: `${percent}%`, height: "100%", borderRadius: fluid(100), backgroundColor: "#594099" }} />
        </div>
        <span style={{ flexShrink: 0, fontFamily: "Roboto, sans-serif", fontWeight: 400, fontSize: fluid(16), letterSpacing: 0, color: "#636363", whiteSpace: "nowrap" }}>
            {percent}% completed
        </span>
    </div>
);

export const QuickStatCell = ({ label, value, style }) => (
    <div style={{
        flex: 1,
        minWidth: 0,
        minHeight: fluid(77),
        backgroundColor: "rgba(255,255,255,0.2)",
        borderRadius: fluid(16),
        padding: fluid(16),
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: fluid(9),
        ...style,
    }}>
        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), fontWeight: 400, letterSpacing: 0, color: "#FFFFFF" }}>{label}</span>
        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 700, letterSpacing: 0, color: "#FFFFFF" }}>{value}</span>
    </div>
);

export const AccountRow = ({ label, value }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: fluid(16) }}>
        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 400, letterSpacing: 0, color: "#666666", whiteSpace: "nowrap" }}>{label}</span>
        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 400, letterSpacing: 0, color: "#1A1A1A", textAlign: "right", whiteSpace: "nowrap" }}>{value || "NA"}</span>
    </div>
);
